import { AxiosResponse } from 'axios';

import { ApiVersion } from '@/config';
import { EthnicOrigin, Gender, Race } from '@/constants/form_inputs';
import {
  IKase,
  IKaseCfsLink,
  IKaseCreateRequest,
  IKaseMasterName,
  IDisposition,
  INarrative,
  INarrativeRequest,
  IKaseRejectRequest,
  IKaseMasterVehicle,
  IPriorityForm,
  IOffense,
  IOffenseRequest,
  IKaseInvolvement,
  IMasterOrganizationRequest,
  IMasterNameOrganization,
  IIncidentInfoForm,
  IArrestee,
  IKaseKaseLink,
  ICTVCode,
  IArresteeRequest,
  IKaseSentForApprovalRequest,
  ITask,
  IKaseMasterNameAggregateResult,
  IKaseRelatedCounts,
  IBulkUpdateItem,
  IBulkUpdateResponse,
} from '@/models';
import { IDomesticIncidentReport } from '@/models/domestic.incident.report';
import { INote } from '@/models/note';

import { http } from './http';
import {
  IGetParams,
  IIndeterminatePaginationResponse,
  IListResponse,
  IPaginatedListResponse,
} from './types';

export const KaseApi = {
  create: async (data: IKaseCreateRequest): Promise<AxiosResponse<IKase>> => {
    return http.post(`api/${ApiVersion.V1}/kases`, data);
  },
  createFromCsf: async (
    data: IKaseCreateRequest,
    cfsId: string,
  ): Promise<AxiosResponse<IKase>> => {
    return http.post(
      `api/${ApiVersion.V1}/kases/create-from-cfs/${cfsId}`,
      data,
    );
  },
  update: async (
    id: string,
    data: Partial<IKaseCreateRequest>,
  ): Promise<AxiosResponse<IKase>> => {
    return http.put(`api/${ApiVersion.V1}/kases/${id}`, data);
  },
  save: async (data: IKaseCreateRequest): Promise<AxiosResponse<IKase>> => {
    return data._id ? KaseApi.update(data._id, data) : KaseApi.create(data);
  },
  getOne: async (id: string): Promise<AxiosResponse<IKase>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${id}`);
  },
  list: async (
    params: IGetParams,
  ): Promise<AxiosResponse<IIndeterminatePaginationResponse<IKase>>> => {
    return http.get(`api/${ApiVersion.V1}/kases`, { params });
  },
  listJmsKases: async (
    params: IGetParams,
  ): Promise<AxiosResponse<IIndeterminatePaginationResponse<IKase>>> => {
    return http.get(`api/${ApiVersion.V1}/kases/jms-kases`, { params });
  },
  listImported: async (
    params: IGetParams,
  ): Promise<AxiosResponse<IIndeterminatePaginationResponse<IKase>>> => {
    return http.get(`api/${ApiVersion.V1}/kases/imported`, { params });
  },
  listCtvCodes: async (
    params: IGetParams,
  ): Promise<AxiosResponse<IListResponse<ICTVCode>>> => {
    return http.get(`api/${ApiVersion.V1}/ctv-codes`, { params });
  },
  getLinkedRelatedCounts: async (
    kaseId: string,
  ): Promise<AxiosResponse<IKaseRelatedCounts>> => {
    return http.get(
      `api/${ApiVersion.V1}/kases/${kaseId}/linked-related-counts`,
    );
  },
  getLinkedMasterNames: async (
    kaseId: string,
    params: IGetParams,
  ): Promise<AxiosResponse<IKaseMasterName[]>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${kaseId}/master-names`, {
      params,
    });
  },
  getLinkedMasterNamesAndOffenses: async (
    kaseId: string,
    params: IGetParams,
  ): Promise<AxiosResponse<IKaseMasterNameAggregateResult[]>> => {
    return http.get(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-names-offenses`,
      {
        params,
      },
    );
  },
  addDisposition: async (
    kaseId: string,
    data: Omit<IDisposition, 'createdBy' | 'createdAt'>,
  ): Promise<AxiosResponse<IKase>> => {
    return http.post(`api/${ApiVersion.V1}/kases/${kaseId}/dispositions`, data);
  },
  setDisposition: async (
    kaseId: string,
    data: Omit<IDisposition, 'createdBy' | 'createdAt'>,
  ): Promise<AxiosResponse<IKase>> => {
    return http.put(`api/${ApiVersion.V1}/kases/${kaseId}/disposition`, data);
  },
  getLinkedCFS: async (
    id: string,
    params?: IGetParams,
  ): Promise<AxiosResponse<IKaseCfsLink[]>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${id}/cfs`, { params });
  },
  linkCFSToKase: async (
    kaseId: string,
    cfsId: string,
  ): Promise<AxiosResponse<IKaseCfsLink>> => {
    return http.post(`api/${ApiVersion.V1}/kases/${kaseId}/cfs/${cfsId}`);
  },
  unlinkCFSFromKase: async (
    kaseId: string,
    cfsId: string,
  ): Promise<AxiosResponse<IKaseCfsLink>> => {
    return http.delete(`api/${ApiVersion.V1}/kases/${kaseId}/cfs/${cfsId}`);
  },
  getLinkedKases: async (
    id: string,
    params: IGetParams,
  ): Promise<AxiosResponse<IKaseKaseLink[]>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${id}/links`, { params });
  },
  getLinkedTasks: async (
    id: string,
    params: IGetParams,
  ): Promise<AxiosResponse<ITask[]>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${id}/related-tasks`, {
      params,
    });
  },
  linkTaskToKase: async (
    kaseId: string,
    taskId: string,
  ): Promise<AxiosResponse<{ ok: true }>> => {
    return http.post(
      `api/${ApiVersion.V1}/kases/${kaseId}/related-tasks/${taskId}`,
    );
  },
  unlinkTaskFromKase: async (
    kaseId: string,
    taskId: string,
  ): Promise<AxiosResponse<{ ok: true }>> => {
    return http.delete(
      `api/${ApiVersion.V1}/kases/${kaseId}/related-tasks/${taskId}`,
    );
  },
  linkKaseToKase: async (
    kaseId: string,
    kaseLinkId: string,
  ): Promise<AxiosResponse<IKaseKaseLink>> => {
    return http.post(
      `api/${ApiVersion.V1}/kases/${kaseId}/links/${kaseLinkId}`,
    );
  },
  unlinkKaseFromKase: async (
    kaseId: string,
    kaseLinkId: string,
  ): Promise<AxiosResponse<IKaseKaseLink>> => {
    return http.delete(
      `api/${ApiVersion.V1}/kases/${kaseId}/links/${kaseLinkId}`,
    );
  },
  getKaseLogs: async (
    kaseId: string,
    params: IGetParams,
  ): Promise<AxiosResponse<any>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${kaseId}/logs`, { params });
  },
  // --------------- narratives ---------------------\\
  saveNarrative: async (
    kaseId: string,
    data: INarrativeRequest,
  ): Promise<AxiosResponse<INarrative>> => {
    return data._id
      ? KaseApi.updateNarrative(kaseId, data._id, data)
      : KaseApi.createNarrative(kaseId, data);
  },
  createNarrative: async (
    kaseId: string,
    data: INarrativeRequest,
  ): Promise<AxiosResponse<INarrative>> => {
    return http.post(`api/${ApiVersion.V1}/kases/${kaseId}/narratives`, data);
  },
  updateNarrative: async (
    kaseId: string,
    narrativeId: string,
    data: INarrativeRequest,
  ): Promise<AxiosResponse<INarrative>> => {
    return http.put(
      `api/${ApiVersion.V1}/kases/${kaseId}/narratives/${narrativeId}`,
      data,
    );
  },
  getLinkedNarratives: async (
    kaseId: string,
    params: IGetParams = { sort: JSON.stringify({ createdAt: 1 }) },
  ): Promise<AxiosResponse<INarrative[]>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${kaseId}/narratives`, {
      params,
    });
  },
  deleteNarrative: async (
    kaseId: string,
    narrativeId: string,
  ): Promise<AxiosResponse<IKaseCfsLink>> => {
    return http.delete(
      `api/${ApiVersion.V1}/kases/${kaseId}/narratives/${narrativeId}`,
    );
  },
  // --------------- arrest narratives ---------------------\\
  getArresteeNarratives: async (
    kaseId: string,
    arresteeId: string,
    params: IGetParams = { sort: JSON.stringify({ createdAt: 1 }) },
  ): Promise<AxiosResponse<INarrative[]>> => {
    return http.get(
      `api/${ApiVersion.V1}/kases/${kaseId}/arrestees/${arresteeId}/narratives`,
      { params },
    );
  },
  createArresteeNarrative: async (
    kaseId: string,
    arresteeId: string,
    data: INarrativeRequest,
  ): Promise<AxiosResponse<INarrative>> => {
    return http.post(
      `api/${ApiVersion.V1}/kases/${kaseId}/arrestees/${arresteeId}/narratives`,
      data,
    );
  },
  updateArresteeNarrative: async (
    kaseId: string,
    arresteeId: string,
    narrativeId: string,
    data: INarrativeRequest,
  ): Promise<AxiosResponse<INarrative>> => {
    return http.put(
      `api/${ApiVersion.V1}/kases/${kaseId}/arrestees/${arresteeId}/narratives/${narrativeId}`,
      data,
    );
  },
  deleteArresteeNarrative: async (
    kaseId: string,
    arresteeId: string,
    narrativeId: string,
  ): Promise<AxiosResponse<any>> => {
    return http.delete(
      `api/${ApiVersion.V1}/kases/${kaseId}/arrestees/${arresteeId}/narratives/${narrativeId}`,
    );
  },
  saveArresteeNarrative: async (
    kaseId: string,
    arresteeId: string,
    data: INarrativeRequest,
  ): Promise<AxiosResponse<INarrative>> => {
    return data._id
      ? KaseApi.updateArresteeNarrative(kaseId, arresteeId, data._id, data)
      : KaseApi.createArresteeNarrative(kaseId, arresteeId, data);
  },
  // --------------- masternames ---------------------\\
  getKaseMasterNames: async (
    kaseId: string,
  ): Promise<AxiosResponse<IKaseMasterName[]>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${kaseId}/master-names`);
  },
  addKaseMasterName: async (
    kaseId: string,
    nameId: string,
  ): Promise<AxiosResponse<IKaseMasterName>> => {
    return http.post(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-names/${nameId}`,
      {},
    );
  },
  createAndLinkUnknownOffender: async (
    kaseId: string,
  ): Promise<AxiosResponse<IKaseMasterName>> => {
    return http.post(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-names/unknown-offender`,
      {},
    );
  },
  updateKaseMasterName: async (
    kaseId: string,
    nameId: string,
    data: {
      relationship?: IKaseInvolvement;
      additionalInfo?: Record<string, any>;
    },
  ): Promise<AxiosResponse<IKaseMasterName>> => {
    return http.patch(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-names/${nameId}`,
      data,
    );
  },
  bulkUpdateKaseMasterName: async (
    kaseId: string,
    items: IBulkUpdateItem[],
  ): Promise<AxiosResponse<IBulkUpdateResponse>> => {
    return http.patch(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-names/bulk-update`,
      { items },
    );
  },
  deleteKaseMasterName: async (
    kaseId: string,
    nameId: string,
  ): Promise<AxiosResponse<IKaseMasterName>> => {
    return http.delete(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-names/${nameId}`,
    );
  },
  getKaseMasterName: async (
    kaseId: string,
    nameId: string,
  ): Promise<AxiosResponse<IKaseMasterName>> => {
    return http.get(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-names/${nameId}`,
    );
  },
  createMasterNameNote: (
    kaseId: string,
    nameId: string,
    description: string,
  ): Promise<AxiosResponse<INote>> => {
    return http.post(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-names/${nameId}/notes`,
      {
        description,
      },
    );
  },

  // --------------- masterName kases---------------------\\
  saveMasterOrganization: async (
    data: IMasterOrganizationRequest,
  ): Promise<AxiosResponse<IMasterNameOrganization>> => {
    return data._id
      ? KaseApi.updateMasterOrganization(data._id, data)
      : KaseApi.addMasterOrganization(data);
  },
  addMasterOrganization: async (
    data: IMasterOrganizationRequest,
  ): Promise<AxiosResponse<IMasterNameOrganization>> => {
    return http.post(`api/${ApiVersion.V1}/master-names/organizations/`, data);
  },
  updateMasterOrganization: async (
    organizationId: string,
    data: IMasterOrganizationRequest,
  ): Promise<AxiosResponse<IMasterNameOrganization>> => {
    return http.put(
      `api/${ApiVersion.V1}/master-names/organizations/${organizationId}`,
      data,
    );
  },
  getMasterOrganization: async (
    organizationId: string,
  ): Promise<AxiosResponse<IMasterNameOrganization>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-names/organizations/${organizationId}`,
    );
  },
  deleteMasterOrganization: async (
    kaseId: string,
    nameId: string,
  ): Promise<AxiosResponse<IKaseMasterName>> => {
    return http.delete(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-names/${nameId}`,
    );
  },
  addUnknownOrganization: async (
    kaseId: string,
  ): Promise<AxiosResponse<IMasterNameOrganization>> => {
    return http.post(
      `api/${ApiVersion.V1}/master-names/organizations/unknown/`,
      { kaseId },
    );
  },
  addUnknownPerson: async (
    kaseId: string,
    defaults?: {
      sex?: Gender;
      race?: Race;
      ethnicOrigin?: EthnicOrigin;
    },
  ): Promise<AxiosResponse<{ kaseId: string; _id: string }>> => {
    return http.post(`api/${ApiVersion.V1}/master-names/persons/unknown/`, {
      kaseId,
      ...defaults,
    });
  },
  //
  // --------------- update case status ---------------------\\
  submitKase: async (
    kaseId: string,
    data: IKaseSentForApprovalRequest,
  ): Promise<AxiosResponse<IKase>> => {
    return http.patch(`api/${ApiVersion.V1}/kases/${kaseId}/submit`, data);
  },
  rejectKase: async (
    kaseId: string,
    data: IKaseRejectRequest,
  ): Promise<AxiosResponse<IKase>> => {
    return http.patch(`api/${ApiVersion.V1}/kases/${kaseId}/reject`, data);
  },
  approveKase: async (
    kaseId: string,
    data: IKaseRejectRequest,
  ): Promise<AxiosResponse<IKase>> => {
    return http.patch(`api/${ApiVersion.V1}/kases/${kaseId}/approve`, data);
  },
  reopenKase: async (kaseId: string): Promise<AxiosResponse<IKase>> => {
    return http.patch(`api/${ApiVersion.V1}/kases/${kaseId}/reopen`);
  },
  updateNibrsExclusion: async (
    kaseId: string,
    isExcludedFromNibrs: boolean,
  ): Promise<AxiosResponse<IKase>> => {
    return http.patch(
      `api/${ApiVersion.V1}/kases/${kaseId}/updateNibrsExclusion`,
      { isExcludedFromNibrs },
    );
  },
  reassignKase: async (
    kaseId: string,
    supervisorId: string,
    comment: string,
  ): Promise<AxiosResponse<IKase>> => {
    return http.patch(
      `api/${ApiVersion.V1}/kases/${kaseId}/assign/${supervisorId}`,
      { comment },
    );
  },
  togglePriority: async (
    kaseId: string,
    data?: IPriorityForm,
  ): Promise<AxiosResponse<IKase>> => {
    return http.patch(
      `api/${ApiVersion.V1}/kases/${kaseId}/toggle-priority`,
      data,
    );
  },
  restrictCase: async (
    kaseId: string,
    data: { caseRestrictedBy: string | null; usersWithCaseAccess: string[] },
  ): Promise<AxiosResponse<IKase>> => {
    return http.put(`api/${ApiVersion.V1}/kases/${kaseId}/restrict`, data);
  },
  // --------------- vehicles ---------------------\\
  getKaseMasterVehicles: async (
    kaseId: string,
  ): Promise<AxiosResponse<IKaseMasterVehicle[]>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${kaseId}/master-vehicles`);
  },
  getPaginatedKaseMasterVehicles: async (
    kaseId: string,
  ): Promise<AxiosResponse<IPaginatedListResponse<IKaseMasterVehicle>>> => {
    return http.get(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-vehicles/list`,
    );
  },
  addKaseMasterVehicle: async (
    kaseId: string,
    vehicleId: string,
  ): Promise<AxiosResponse<IKaseMasterVehicle>> => {
    return http.post(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-vehicles/${vehicleId}`,
    );
  },
  deleteKaseMasterVehicle: async (
    kaseId: string,
    vehicleId: string,
  ): Promise<AxiosResponse<IKaseMasterVehicle>> => {
    return http.delete(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-vehicles/${vehicleId}`,
    );
  },
  // --------------- offenses ---------------------\\
  getOffenses: async (
    kaseId: string,
    params?: IGetParams,
  ): Promise<AxiosResponse<IListResponse<IOffense>>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${kaseId}/offenses`, {
      params,
    });
  },
  createOffense: async (
    kaseId: string,
    data: IOffenseRequest,
  ): Promise<AxiosResponse<IOffense>> => {
    return http.post(`api/${ApiVersion.V1}/kases/${kaseId}/offenses`, data);
  },
  updateOffense: async (
    kaseId: string,
    { _id: offenseId, ...data }: IOffenseRequest,
  ): Promise<AxiosResponse<IOffense>> => {
    return http.put(
      `api/${ApiVersion.V1}/kases/${kaseId}/offenses/${offenseId}`,
      data,
    );
  },
  deleteOffense: async (
    kaseId: string,
    offenseId: string,
  ): Promise<AxiosResponse<IOffense>> => {
    return http.delete(
      `api/${ApiVersion.V1}/kases/${kaseId}/offenses/${offenseId}`,
    );
  },
  updateIncidentInfo: async (kaseId: string, data: IIncidentInfoForm) => {
    return http.patch(
      `api/${ApiVersion.V1}/kases/${kaseId}/incident-info`,
      data,
    );
  },
  // --------------- arrestee ---------------------\\
  getArrestees: async (
    kaseId: string,
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<IArrestee>>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${kaseId}/arrestees`, {
      params,
    });
  },
  getArrestee: async (
    kaseId: string,
    masterNameId: string,
  ): Promise<AxiosResponse<IArrestee>> => {
    return http.get(
      `api/${ApiVersion.V1}/kases/${kaseId}/arrestees/${masterNameId}`,
    );
  },
  updateArrestee: async (
    kaseId: string,
    masterNameId: string,
    data: IArresteeRequest,
  ): Promise<AxiosResponse<IArrestee>> => {
    return http.put(
      `api/${ApiVersion.V1}/kases/${kaseId}/arrestees/${masterNameId}`,
      data,
    );
  },
  updateArresteeArrestStatus: async (
    kaseMasterNameId: string,
    data: number,
  ): Promise<AxiosResponse<IArrestee>> => {
    return http.patch(
      `api/${ApiVersion.V1}/kases/master-names/${kaseMasterNameId}/arrest-status`,
      { wasArrested: data },
    );
  },
  getArresteeOffenseHistory: async (
    kaseId: string,
    arresteeId: string,
  ): Promise<AxiosResponse<any[]>> => {
    return http.get(
      `api/${ApiVersion.V1}/kases/${kaseId}/arrestees/${arresteeId}/offense-history`,
    );
  },

  // --------------- NIBRS report---------------------\\
  getNibrsReport: async (kaseId: string): Promise<AxiosResponse<any>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${kaseId}/generate-report`);
  },
  listNibrsReport: async (kaseId: string): Promise<AxiosResponse<any>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${kaseId}/nibrs-report`);
  },
  createNibrsReport: async (kaseId: string): Promise<AxiosResponse<any>> => {
    return http.post(`api/${ApiVersion.V1}/kases/${kaseId}/nibrs-report`);
  },
  createNibrsBulkReport: async (
    startDate: string,
    endDate: string,
  ): Promise<AxiosResponse<{ content: string }[]>> => {
    return http.post(`api/${ApiVersion.V1}/kases/nibrs-bulk-report`, {
      startDate,
      endDate,
    });
  },

  getNibrsCases: async (
    startDate: string,
    endDate: string,
  ): Promise<
    AxiosResponse<
      {
        _id: string;
        number: string;
        createdBy: { fullName: string };
        approvedInfo: { approvedAt: string }[];
        assignmentInfo: { assignedTo: { fullName: string } }[];
      }[]
    >
  > => {
    return http.post(`api/${ApiVersion.V1}/kases/nibrs-get-month-cases`, {
      startDate,
      endDate,
    });
  },

  // --------------- Domestic incident report ---------------------\\
  print: async (
    kaseId: string,
    data: IDomesticIncidentReport,
    includeHelpSheet: boolean,
  ): Promise<AxiosResponse<any>> => {
    return http.post(
      `api/${ApiVersion.V1}/kases/${kaseId}/print-domestic-incident-report`,
      { ...data, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      {
        params: { includeHelpSheet },
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf',
        },
      },
    );
  },

  saveDraft: async (
    kaseId: string,
    data: IDomesticIncidentReport,
    draftId?: string,
  ): Promise<AxiosResponse<IKase>> => {
    if (draftId) {
      return http.put(
        `api/${ApiVersion.V1}/kases/${kaseId}/drafts/${draftId}`,
        data,
      );
    }
    return http.post(`api/${ApiVersion.V1}/kases/${kaseId}/drafts`, data);
  },
  deleteDraft: async (
    kaseId: string,
    draftId?: string,
  ): Promise<AxiosResponse<IKase>> => {
    return http.delete(
      `api/${ApiVersion.V1}/kases/${kaseId}/drafts/${draftId}`,
    );
  },

  initiateFingerprintScan: async (
    kaseId: string,
    arresteeId: string,
  ): Promise<AxiosResponse<any>> => {
    return http.post(
      `api/${ApiVersion.V1}/kases/${kaseId}/arrestees/${arresteeId}/dataworks-export`,
    );
  },

  // --------------- CFS Sync ---------------------\\
  /**
   * Get the sync status of a Kase with its linked CFS
   */
  getSyncStatus: async (kaseId: string): Promise<AxiosResponse<any>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${kaseId}/sync-status`);
  },

  /**
   * Check the diff between a Kase and a CFS
   */
  checkSyncDiff: async (
    kaseId: string,
    cfsId: string,
  ): Promise<AxiosResponse<any>> => {
    return http.get(`api/${ApiVersion.V1}/kases/${kaseId}/sync-diff/${cfsId}`);
  },

  /**
   * Sync specific fields from a CFS to a Kase
   */
  syncFromCFS: async (
    kaseId: string,
    cfsId: string,
    fieldsToSync: string[],
  ): Promise<AxiosResponse<IKase>> => {
    return http.post(`api/${ApiVersion.V1}/kases/${kaseId}/sync-cfs/${cfsId}`, {
      fieldsToSync,
    });
  },
};
